import { resolveTagSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigation';

const CLICK_BEHAVIOR = Object.freeze({
  LOCAL: 'local',
  NAVIGATE_SINGLE: 'navigate-single',
  NAVIGATE_MENU: 'navigate-menu',
  NONE: 'none',
});

export function popup360FallbackConfirmation() {
  return {
    title: 'Saved 360 image unavailable',
    text: 'The saved 360 image can no longer be opened. Open the nearest available panorama instead?',
    persistent: true,
    actions: {
      false: 'Cancel',
      true: { text: 'Open nearest', color: 'primary' },
    },
  };
}

const isScalarId = value => (typeof value === 'string' && value.trim() !== '')
  || (typeof value === 'number' && Number.isFinite(value));

const canonicalId = value => (typeof value === 'string' ? value.trim() : value);

const sameId = (left, right) => isScalarId(left)
  && isScalarId(right)
  && String(left).toLowerCase() === String(right).toLowerCase();

const hasFinitePoint = point => point
  && typeof point === 'object'
  && Number.isFinite(point.x)
  && Number.isFinite(point.y)
  && Number.isFinite(point.z);

function tagPoint(tag) {
  const identities = [tag?.type, tag?.discriminator]
    .filter(identity => identity !== null && identity !== undefined)
    .map(identity => String(identity).toLowerCase());
  const isScaffoldFamily = identities.some(identity => identity.includes('scaffold')
    || identity.includes('permit')
    || identity.includes('container'));
  return isScaffoldFamily
    ? tag?.position
    : tag?.point;
}

function accessibleFacilityEntries(facilityDict, currentFacilityId) {
  return Object.entries(facilityDict || {})
    .filter(([id]) => isScalarId(id) && !sameId(id, currentFacilityId))
    .map(([id, name]) => ({ id: canonicalId(id), name: name || canonicalId(id) }));
}

function isRemoteViewerPlan(plan, facilityId, viewer) {
  return plan.kind === 'navigate-facility'
    && plan.viewer === viewer
    && sameId(plan.facilityId, facilityId);
}

/**
 * Can this OTHER facility open the tag in 360?
 *
 * The rule is the same one the local button follows: the linked scan first, the nearest scan as
 * the fallback. A linked scan is a PRIORITY, never a requirement - so asking a remote facility for
 * one and giving up when it has none was the wrong question, the mirror of the bug the local side
 * had. A tag anchored in that facility can always be opened at its nearest scan there.
 *
 * "Anchored" is either half of a 3D plan: an explicit point placement (`point-3d`) or the model
 * elements it was harvested onto. The two are the only provenances a 3D plan carries, so between
 * them they mean "the tag is somewhere in that facility's model".
 *
 * Whether that facility actually has scans is not answerable from here - we only hold the current
 * facility's catalogue - and it does not need to be: navigating opens that facility, which resolves
 * its own scans the same way this one does.
 */
function hasAuthorizedRemote360(tag, facilityId, resolverContext) {
  const panoramaPlan = resolveTagSpatialIntent(tag, {
    ...resolverContext,
    selectedFacilityId: facilityId,
    intent: 'explicit-360',
  });
  if (isRemoteViewerPlan(panoramaPlan, facilityId, '360')) return true;
  const pointPlan = resolveTagSpatialIntent(tag, {
    ...resolverContext,
    selectedFacilityId: facilityId,
    intent: 'explicit-3d',
  });
  return isRemoteViewerPlan(pointPlan, facilityId, '3d')
    && (pointPlan.provenance === 'point-3d' || pointPlan.provenance === 'model-elements');
}

function authoritativeRemoteFacilities(tag, context, knownFacilities) {
  const resolverContext = {
    currentFacilityId: context.currentFacilityId,
    accessibleFacilityIds: [context.currentFacilityId, ...knownFacilities.map(({ id }) => id)],
  };
  return knownFacilities.filter(({ id }) => hasAuthorizedRemote360(
    tag,
    id,
    resolverContext,
  ));
}

function legacyRemoteFacilities(tag, knownFacilities) {
  if (tag?.isMaster !== true || ownerScope(tag).kind === 'invalid') return [];
  const advertisedIds = [
    ...(Array.isArray(tag?.otherPositionedFacilityIds) ? tag.otherPositionedFacilityIds : []),
    ...(Array.isArray(tag?.otherAvailableAssets) ? tag.otherAvailableAssets : []),
  ];
  return knownFacilities.filter(({ id }) => advertisedIds.some(advertisedId => sameId(
    advertisedId,
    id,
  )));
}

function exactLocalAction(tag, context, accessibleFacilityIds) {
  const plan = resolveTagSpatialIntent(tag, {
    currentFacilityId: context.currentFacilityId,
    currentFacilityImageIds: context.currentFacilityImages
      .map(image => image?.id)
      .filter(isScalarId),
    accessibleFacilityIds,
    intent: 'explicit-360',
  });
  if (plan.kind !== 'viewer' || plan.viewer !== '360'
    || !sameId(plan.facilityId, context.currentFacilityId)) return null;
  if (!currentCatalogContains(context, plan.image360Id)) return null;
  return {
    kind: 'exact',
    image360Id: plan.image360Id,
    provenance: plan.provenance,
  };
}

function currentCatalogContains(context, image360Id) {
  return isScalarId(image360Id)
    && context.currentFacilityImages.some(image => sameId(image?.id, image360Id));
}

function legacyCatalogExactAction(tag, context) {
  if (Object.prototype.hasOwnProperty.call(tag || {}, 'spatialTargets')
    || !hasFinitePoint(tagPoint(tag))
    || !legacyHasScopedPoint(tag, context.currentFacilityId)) return null;
  if (isScalarId(tag?.positionImage360Id)) {
    return currentCatalogContains(context, tag.positionImage360Id)
      ? {
        kind: 'exact',
        image360Id: canonicalId(tag.positionImage360Id),
        provenance: 'facility-scan-position',
      }
      : null;
  }
  const linkedImageId = tag?.linked360ImageInfo?.imageId;
  return currentCatalogContains(context, linkedImageId)
    ? { kind: 'exact', image360Id: canonicalId(linkedImageId), provenance: 'linked-source' }
    : null;
}

function legacyHasScopedPoint(tag, currentFacilityId) {
  const owner = ownerScope(tag);
  if (owner.kind === 'invalid') return false;
  if (owner.kind === 'present') return sameId(owner.id, currentFacilityId);
  const positionedHere = Array.isArray(tag?.positionedFacilityIds)
    && tag.positionedFacilityIds.some(id => sameId(id, currentFacilityId));
  if (positionedHere) return true;
  return isScalarId(tag?.facilityGroupId)
    && tag?.isMaster === true
    && tag?.isHarvestedInCurrentFacility === true
    && Array.isArray(tag?.children)
    && tag.children.length > 0;
}

function ownerScope(tag) {
  const ownerId = tag?.forgeAssetId;
  if (ownerId === null || ownerId === undefined || ownerId === '') return { kind: 'absent' };
  if (!isScalarId(ownerId)) return { kind: 'invalid' };
  return { kind: 'present', id: canonicalId(ownerId) };
}

/**
 * Does this facility have a point to find the nearest scan to?
 *
 * `hasPoint` is the answer; `provenance` only says where the point CAME FROM. Asking for
 * `point-3d` meant a HARVESTED master was refused: the resolver describes its current-facility
 * target as `model-elements` and still sets `hasPoint: true` when this facility owns the base
 * point (TagFacilityOverrideResolver), so the tag had a perfectly good point and the popup's 360
 * button vanished. Tags placed by hand kept working, which is what made it look like a data
 * problem rather than this test.
 *
 * Viewer is not asked either: a `facility-scan-position` target is a point as well. That case is
 * already answered by exactLocalAction above, so reaching here with one simply means the exact
 * scan was unusable and the nearest is the right fallback.
 *
 * MODEL ELEMENTS COUNT TOO, and this is the part that is easy to get wrong. For a Facility
 * Group-owned MASTER the server deliberately attributes its base point to NO facility
 * (TagFacilityOverrideResolver.AuthoritativeBasePointFacilityId returns null for a group-owned
 * master), because that one point is shared and claiming it for a member would be a lie. So a
 * master harvested in two facilities arrives with `hasPoint: false` on BOTH of them while still
 * carrying a perfectly good point - and the 360 button disappeared on exactly the tags the group
 * feature exists for. That server rule is correct and is not worked around here.
 *
 * What the nearest-scan search actually needs is a point in this facility to search FROM, and
 * `hasModelElements` says the tag is physically in this facility's model. The coordinates come
 * from the caller's own guard: nearestLocalAction requires a finite point before asking this, and
 * an empty scan catalogue still refuses.
 */
function authoritativeHasCurrentPoint(tag, currentFacilityId) {
  return (Array.isArray(tag?.spatialTargets) ? tag.spatialTargets : []).some(target => (
    sameId(target?.facilityId, currentFacilityId)
    && (target?.hasPoint === true || target?.hasModelElements === true)
  ));
}

function missingExactAction(tag, context, accessibleFacilityIds) {
  if (!Object.prototype.hasOwnProperty.call(tag || {}, 'spatialTargets')) {
    const image360Id = isScalarId(tag?.positionImage360Id)
      ? tag.positionImage360Id
      : tag?.linked360ImageInfo?.imageId;
    if (!isScalarId(image360Id) || currentCatalogContains(context, image360Id)) return null;
    return {
      kind: 'confirm-nearest',
      image360Id: canonicalId(image360Id),
      provenance: isScalarId(tag?.positionImage360Id)
        ? 'facility-scan-position'
        : 'linked-source',
    };
  }
  const plan = resolveTagSpatialIntent(tag, {
    currentFacilityId: context.currentFacilityId,
    accessibleFacilityIds,
    intent: 'explicit-360',
  });
  if (plan.kind !== 'viewer' || plan.viewer !== '360'
    || currentCatalogContains(context, plan.image360Id)) return null;
  return {
    kind: 'confirm-nearest',
    image360Id: plan.image360Id,
    provenance: plan.provenance,
  };
}

function nearestLocalAction(tag, context, accessibleFacilityIds) {
  if (!hasFinitePoint(tagPoint(tag)) || context.currentFacilityImages.length === 0) return null;
  const hasAuthoritativeContract = Object.prototype.hasOwnProperty.call(tag || {}, 'spatialTargets');
  const unavailableExact = missingExactAction(tag, context, accessibleFacilityIds);
  const scopedPoint = hasAuthoritativeContract
    ? (unavailableExact || authoritativeHasCurrentPoint(tag, context.currentFacilityId))
    : legacyHasScopedPoint(tag, context.currentFacilityId);
  if (!scopedPoint) return null;
  return unavailableExact || { kind: 'nearest' };
}

function tooltipFor(localAction, otherFacilities) {
  if (localAction) {
    return otherFacilities.length > 0
      ? 'Show in 360 (right-click for other facilities)'
      : 'Show in 360';
  }
  if (otherFacilities.length === 1) return `Open in ${otherFacilities[0].name}`;
  if (otherFacilities.length > 1) return 'Open in another facility';
  return 'Show in 360';
}

function popupClickBehavior(localAction, otherFacilities) {
  if (localAction) return CLICK_BEHAVIOR.LOCAL;
  if (otherFacilities.length === 1) return CLICK_BEHAVIOR.NAVIGATE_SINGLE;
  if (otherFacilities.length > 1) return CLICK_BEHAVIOR.NAVIGATE_MENU;
  return CLICK_BEHAVIOR.NONE;
}

function isExcludedLevel(tag, otherFacilities) {
  const identities = [tag?.type, tag?.discriminator]
    .filter(value => value !== null && value !== undefined)
    .map(value => String(value).toLowerCase());
  return !!tag?.level2D
    && !identities.some(identity => identity.includes('avainspect'))
    && otherFacilities.length === 0;
}

function popupContext(suppliedContext) {
  return {
    currentFacilityId: suppliedContext.currentFacilityId ?? null,
    currentFacilityImages: Array.isArray(suppliedContext.currentFacilityImages)
      ? suppliedContext.currentFacilityImages
      : [],
    facilityDict: suppliedContext.facilityDict || {},
  };
}

/**
 * The typed tag read with the spatial contract of the facility-scoped read.
 *
 * `GET_BASE_TAG_BY_ID` is asked for one facility, so its `spatialTargets` say where the tag can
 * open THERE. The typed reads (`FETCH_TAG_BY_TYPE_AND_ID`) carry no facility: for General /
 * Annotation and Inspection tags they return `spatialTargets: []`, which resolvePopup360State
 * reads as "authoritatively nowhere" - the zoom360 deep link then failed with "Unable to open this
 * tag in the requested viewer" for a tag with a perfectly good linked scan. The typed read adds
 * details; it never decides where the tag is. Neither input is changed.
 */
export function withScopedSpatialTargets(typedTag, scopedTag) {
  if (!typedTag) return scopedTag;
  if (!Object.prototype.hasOwnProperty.call(scopedTag || {}, 'spatialTargets')) return typedTag;
  return Object.assign(
    Object.create(Object.getPrototypeOf(typedTag)),
    typedTag,
    { spatialTargets: scopedTag.spatialTargets },
  );
}

/**
 * Resolves the popup/Global Search 360 affordance without executing navigation.
 * New API spatialTargets stay authoritative; legacy recovery remains scoped to
 * the current facility and to accessible facility ids already known by the UI.
 */
export function resolvePopup360State(tag, suppliedContext = {}) {
  const context = popupContext(suppliedContext);
  const knownFacilities = accessibleFacilityEntries(context.facilityDict, context.currentFacilityId);
  const accessibleFacilityIds = [context.currentFacilityId, ...knownFacilities.map(({ id }) => id)];
  const hasAuthoritativeContract = Object.prototype.hasOwnProperty.call(tag || {}, 'spatialTargets');
  const otherFacilities = hasAuthoritativeContract
    ? authoritativeRemoteFacilities(tag, context, knownFacilities)
    : legacyRemoteFacilities(tag, knownFacilities);
  const localAction = legacyCatalogExactAction(tag, context)
    || exactLocalAction(tag, context, accessibleFacilityIds)
    || nearestLocalAction(tag, context, accessibleFacilityIds);
  const clickBehavior = popupClickBehavior(localAction, otherFacilities);

  return {
    localAction,
    otherFacilities,
    clickBehavior,
    buttonColor: otherFacilities.length > 0 ? 'warning' : '',
    tooltip: tooltipFor(localAction, otherFacilities),
    visible: !isExcludedLevel(tag, otherFacilities) && clickBehavior !== CLICK_BEHAVIOR.NONE,
  };
}

function executionTagForScan(tag, image360Id) {
  return Object.assign(
    Object.create(Object.getPrototypeOf(tag || {})),
    tag,
    {
      point: tagPoint(tag),
      positionImage360Id: image360Id,
    },
  );
}

function isUnavailableScanError(err) {
  return err?.code === 'SCAN_UNAVAILABLE'
    || err?.code === 'IMAGE_NOT_FOUND'
    || err?.status === 404;
}

async function navigateToPanorama(dependencies, action) {
  let actionResult;
  const run = async () => {
    actionResult = await action();
    return actionResult;
  };
  const navigationResult = typeof dependencies.navigateToPanorama === 'function'
    ? await dependencies.navigateToPanorama(run)
    : await run();
  return navigationResult ?? actionResult;
}

export async function navigatePopup360ToPanorama(navigateToViewer, panoramaAction) {
  let actionOutcome;
  let actionError;
  const navigationOutcome = await navigateToViewer(async () => {
    try {
      actionOutcome = await panoramaAction();
      return actionOutcome;
    } catch (err) {
      actionError = err;
      throw err;
    }
  });
  if (actionError) throw actionError;
  return navigationOutcome ?? actionOutcome;
}

async function openNearestPanorama(execution) {
  const {
    tag, currentFacilityId, shouldExecute, requestOwner, dependencies,
  } = execution;
  if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
  const nearestImage = await dependencies.getNearestImage?.(tag);
  if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
  if (!isScalarId(nearestImage?.id)) {
    dependencies.notifyUnavailable?.('No 360 scan is available near this tag.');
    return { status: 'unavailable', reason: 'no-nearest-360' };
  }
  return navigateToPanorama(dependencies, () => dependencies.openAtScanPosition?.(
    executionTagForScan(tag, nearestImage.id),
    {
      facilityId: currentFacilityId,
      image360Id: nearestImage.id,
      allowNearestFallback: false,
      rejectOnUnavailable: true,
      shouldExecute,
      requestOwner,
      onReady: dependencies.onPanoramaReady,
      // The nearest scan is where to look from, not where the tag was placed.
      highlightTag: tag,
    },
  ));
}

async function confirmNearestFallback(execution) {
  const { shouldExecute, dependencies } = execution;
  if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
  const confirmed = await dependencies.confirmNearest?.();
  if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
  if (!confirmed) return { status: 'cancelled', reason: 'nearest-fallback-declined' };
  return openNearestPanorama(execution);
}

async function openExactPanorama(execution, action) {
  const {
    tag, currentFacilityId, shouldExecute, requestOwner, dependencies,
  } = execution;
  const openExact = action.provenance === 'linked-source'
    ? () => dependencies.openAtLinkedSource?.(tag, {
      facilityId: currentFacilityId,
      image360Id: action.image360Id,
      allowNearestFallback: false,
      shouldExecute,
      requestOwner,
      onReady: dependencies.onPanoramaReady,
    })
    : () => dependencies.openAtScanPosition?.(
      executionTagForScan(tag, action.image360Id),
      {
        facilityId: currentFacilityId,
        image360Id: action.image360Id,
        allowNearestFallback: false,
        rejectOnUnavailable: true,
        shouldExecute,
        requestOwner,
        onReady: dependencies.onPanoramaReady,
        highlightTag: tag,
      },
    );
  return navigateToPanorama(dependencies, openExact);
}

/**
 * Executes the popup's explicit 360 choice. Exact scans never degrade silently;
 * a missing exact association must be acknowledged before nearest-scan fallback.
 */
export async function executePopup360Action({
  tag,
  action,
  currentFacilityId,
  shouldExecute: suppliedShouldExecute,
  dependencies = {},
} = {}) {
  const shouldExecute = typeof suppliedShouldExecute === 'function'
    ? suppliedShouldExecute
    : () => true;
  const requestOwner = {};
  const execution = {
    tag, currentFacilityId, shouldExecute, requestOwner, dependencies,
  };
  if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
  if (action?.kind === 'nearest') {
    return openNearestPanorama(execution);
  }
  if (action?.kind === 'confirm-nearest') {
    return confirmNearestFallback(execution);
  }
  if (action?.kind !== 'exact' || !isScalarId(action.image360Id)) {
    return { status: 'unavailable', reason: 'no-popup-360-action' };
  }

  try {
    return await openExactPanorama(execution, action);
  } catch (err) {
    if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
    if (!isUnavailableScanError(err)) throw err;
    return confirmNearestFallback(execution);
  }
}
